import { z } from 'zod'

import { PublicImageSchema } from './public'
import { SHOP_CURRENCY, SHOP_LIMITS, ShopTagSchema, SkuSchema } from './shop'
import { PromoHintSchema, PromoQuoteSchema } from './shop-promos'

// Public read model of the online store and the cart quote (see shop.ts). Separate from shop.ts, which content.ts
// imports, so the public schemas do not form an import cycle with content.ts.

// ── Public (locale-resolved) ─────────────────────────────────────────────
export const PublicShopColorSchema = z.object({ key: z.string(), name: z.string(), hex: z.string(), images: z.array(PublicImageSchema) })
export type PublicShopColor = z.infer<typeof PublicShopColorSchema>

export const PublicShopVariantSchema = z.object({
  sku: z.string(),
  color: z.string(),
  size: z.string(),
  stock: z.number().int(),
  /** "Only N left" at or under this stock (already capped so it never fires on the capped stock number). */
  lowStock: z.number().int().default(SHOP_LIMITS.lowStock),
})
export type PublicShopVariant = z.infer<typeof PublicShopVariantSchema>

export const PublicShopProductSchema = z.object({
  slug: z.string(),
  categorySlug: z.string(),
  title: z.string(),
  summary: z.string(),
  price: z.number().int(),
  compareAtPrice: z.number().int().nullable(),
  tags: z.array(ShopTagSchema),
  colors: z.array(PublicShopColorSchema),
  sizes: z.array(z.string()),
  /** Stock capped at SHOP_LIMITS.stockShown. */
  variants: z.array(PublicShopVariantSchema),
  publishedAt: z.string().nullable(),
})
export type PublicShopProduct = z.infer<typeof PublicShopProductSchema>

export const PublicSizeGuideSchema = z.object({ columns: z.array(z.string()), rows: z.array(z.object({ size: z.string(), values: z.array(z.string()) })), note: z.string() })
export type PublicSizeGuide = z.infer<typeof PublicSizeGuideSchema>

export const PublicShopCategorySchema = z.object({
  slug: z.string(),
  title: z.string(),
  tagline: z.string(),
  summary: z.string(),
  image: PublicImageSchema.nullable(),
  sizeGuide: PublicSizeGuideSchema.nullable(),
})
export type PublicShopCategory = z.infer<typeof PublicShopCategorySchema>

/** GET /api/public/shop: the whole priced catalogue, read once per build. `enabled: false` (and nothing else) without the flag. */
export const PublicShopSchema = z.object({
  enabled: z.boolean(),
  /** A sales demo (API var SHOP_DEMO): the demo notice also says that nothing is really sold and data is wiped every night. */
  demo: z.boolean().default(false),
  currency: z.literal(SHOP_CURRENCY).default(SHOP_CURRENCY),
  categories: z.array(PublicShopCategorySchema).default([]),
  products: z.array(PublicShopProductSchema).default([]),
})
export type PublicShop = z.infer<typeof PublicShopSchema>

// ── Cart quote ───────────────────────────────────────────────────────────
/** POST /api/shop/cart/quote. The browser only says what it wants; every price and stock number comes back from the server. */
export const CartQuoteRequestSchema = z.object({
  items: z
    .array(z.object({ sku: SkuSchema, qty: z.number().int().min(1).max(SHOP_LIMITS.qtyPerLine) }))
    .max(SHOP_LIMITS.cartLines),
  locale: z.enum(['en', 'id', 'zh', 'vi', 'ja']).optional(),
  /** A discount code the visitor typed; the server checks it and answers in `promo`. */
  code: z.string().trim().max(40).optional(),
})
export type CartQuoteRequest = z.infer<typeof CartQuoteRequestSchema>

/** `ok`; `reduced`: fewer in stock than asked, qty lowered; `sold_out`; `unavailable`: unknown, unpublished or no longer sold. */
export const CartLineStatusSchema = z.enum(['ok', 'reduced', 'sold_out', 'unavailable'])
export type CartLineStatus = z.infer<typeof CartLineStatusSchema>

export const CartQuoteLineSchema = z.object({
  sku: z.string(),
  status: CartLineStatusSchema,
  qty: z.number().int(),
  requestedQty: z.number().int(),
  stock: z.number().int(),
  unitPrice: z.number().int(),
  compareAtPrice: z.number().int().nullable(),
  lineTotal: z.number().int(),
  product: z
    .object({
      slug: z.string(),
      categorySlug: z.string(),
      title: z.string(),
      color: z.string(),
      colorHex: z.string(),
      size: z.string(),
      image: PublicImageSchema.nullable(),
    })
    .nullable(),
})
export type CartQuoteLine = z.infer<typeof CartQuoteLineSchema>

export const CartQuoteSchema = z.object({
  currency: z.literal(SHOP_CURRENCY),
  lines: z.array(CartQuoteLineSchema),
  subtotal: z.number().int(),
  itemCount: z.number().int(),
  quotedAt: z.string(),
  /** The code asked about, applied or refused; null without one. */
  promo: PromoQuoteSchema.nullable().default(null),
  /** Codes the shop suggests in the cart. */
  hints: z.array(PromoHintSchema).default([]),
})
export type CartQuote = z.infer<typeof CartQuoteSchema>
